'use client';

import Link from 'next/link';
import { useState, useRef } from 'react';
import type { NavItem, NavMegaSection, NavLink as NavLinkType } from '@/lib/content';

export function NavMenu({ items }: { items: NavItem[] }) {
  const [activeLabel, setActiveLabel] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function open(label: string) {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActiveLabel(label);
  }

  function deferClose() {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    // Slight delay so cursor can travel from trigger → panel without flicker
    closeTimer.current = setTimeout(() => setActiveLabel(null), 150);
  }

  const activeItem = items.find((i) => i.label === activeLabel);

  return (
    <>
      <nav className="hidden md:flex items-stretch h-full">
        {items.map((item, i) => (
          <NavTrigger
            key={item.href + item.label}
            item={item}
            isFirst={i === 0}
            isActive={activeLabel === item.label}
            onOpen={() => open(item.label)}
            onClose={deferClose}
          />
        ))}
      </nav>

      {/* Mega-menu drawer — single shared element, position absolute below header */}
      {activeItem && (activeItem.mega || activeItem.children) && (
        <div
          className="absolute top-full left-0 right-0 bg-bone border-t border-ink/10 shadow-[0_16px_40px_-20px_rgba(14,20,32,0.18)] z-40 hidden md:block"
          onMouseEnter={() => open(activeItem.label)}
          onMouseLeave={deferClose}
        >
          {activeItem.mega ? (
            <MegaPanel sections={activeItem.mega} />
          ) : (
            <SimplePanel links={activeItem.children!} />
          )}
        </div>
      )}
    </>
  );
}

function NavTrigger({
  item,
  isFirst,
  isActive,
  onOpen,
  onClose,
}: {
  item:     NavItem;
  isFirst:  boolean;
  isActive: boolean;
  onOpen:   () => void;
  onClose:  () => void;
}) {
  const hasPanel = !!(item.mega?.length || item.children?.length);

  return (
    <div
      className={[
        'relative flex items-stretch',
        isActive ? 'bg-ink/[0.025]' : '',
      ].join(' ')}
      onMouseEnter={hasPanel ? onOpen : undefined}
      onMouseLeave={hasPanel ? onClose : undefined}
    >
      <Link
        href={item.href}
        className="px-3 lg:px-4 h-full inline-flex items-center text-[13px] font-medium text-ink/80 hover:text-ink transition-colors whitespace-nowrap"
        aria-haspopup={hasPanel || undefined}
        aria-expanded={hasPanel ? isActive : undefined}
      >
        {item.label}
      </Link>
    </div>
  );
}

// Mega-menu panel — Kilian-style 4-column drawer with hero image per column
function MegaPanel({ sections }: { sections: NavMegaSection[] }) {
  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10 lg:py-12">
      <div className="grid gap-6 lg:gap-8 grid-cols-2 lg:grid-cols-4">
        {sections.map((section) => (
          <div key={section.title} className="group/panel">
            {/* Hero image — gradient placeholder with editorial label when
                no photo is provided. Photo gets a subtle zoom on hover. */}
            <div
              className={`relative aspect-[4/3] mb-5 overflow-hidden bg-gradient-to-br ${
                section.image.gradient ?? 'from-bone to-ink/[0.04]'
              }`}
            >
              {section.image.src ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={section.image.src}
                  alt={section.image.alt ?? ''}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover/panel:scale-[1.04]"
                />
              ) : section.image.label ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <span
                    aria-hidden="true"
                    className="font-display italic text-ink/30 text-3xl lg:text-4xl tracking-[-0.02em] select-none"
                  >
                    {section.image.label}
                  </span>
                </div>
              ) : null}
            </div>

            <h3 className="text-xs uppercase tracking-[0.14em] font-bold text-ink mb-4">
              {section.title}
            </h3>

            <ul className="space-y-2.5">
              {section.links.map((link) => (
                <li key={link.href + link.label}>
                  <MegaLink link={link} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function MegaLink({ link }: { link: NavLinkType }) {
  return (
    <Link
      href={link.href}
      className={[
        'inline-flex items-center gap-2 text-sm transition-colors',
        link.featured
          ? 'text-ink/65 hover:text-gold font-medium'
          : 'text-ink/80 hover:text-ink',
      ].join(' ')}
    >
      <span>{link.label}</span>
      {link.badge === 'new' && (
        <span className="text-[8px] uppercase tracking-[0.1em] font-bold text-rose translate-y-[-3px]">
          new
        </span>
      )}
      {link.badge === 'sale' && (
        <span className="text-[8px] uppercase tracking-[0.1em] font-bold text-ink/65 translate-y-[-3px]">
          sale
        </span>
      )}
    </Link>
  );
}

// Simple text-only dropdown (used when an item has children but no mega)
function SimplePanel({ links }: { links: NavLinkType[] }) {
  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-6">
      <ul className="grid grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
        {links.map((link) => (
          <li key={link.href + link.label}>
            <Link
              href={link.href}
              className="text-sm text-ink/80 hover:text-ink transition-colors"
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
